//
// # Draw Wire Mode
//

import { Point, schdata } from "GenAlphaModel";
import { WireSegment } from "DrawCore/src/content/schematic/wire";
import { Wires } from "DrawCore/src/content/schematic/wires";
import { ModeHandler } from "../../../modes/";
import { Keys } from "../../../keys";
import type { Schematic } from "../schematic";
import {
  buildPinLocationCache,
  snapToNearbyPin,
  type PinLocationCache,
} from "DrawCore/src/routing";

export class DrawWire extends ModeHandler {
  readonly segments: Array<WireSegment> = [];
  // Cache of pin locations for magnetic snapping
  readonly pinCache: PinLocationCache;

  constructor(readonly schematic: Schematic) {
    super();
    // Build pin cache once when entering draw wire mode.
    // Note: If pins are added/removed while in draw wire mode, the cache
    // becomes stale. This is acceptable since pin changes during wire drawing
    // are rare and the user can exit and re-enter draw wire mode if needed.
    this.pinCache = buildPinLocationCache(schematic.render);
  }

  // Apply magnetic snap to a point if within range of a pin
  private applyPinSnap(point: Point): Point {
    return snapToNearbyPin(point, this.pinCache) ?? point;
  }

  get editor() {
    return this.schematic.editor;
  }

  // Get the last segment in the path.
  // Generally this is the one that gets updated interactively.
  get lastSeg(): WireSegment {
    return this.segments[this.segments.length - 1];
  }

  // Enter the `DrawWire` mode, and create the provisional Wire.
  static start(
    schematic: Schematic,
    initialPosition?: { x: number; y: number }
  ): DrawWire {
    const start = initialPosition ?? schematic.editor.gridPointNearestToMouse();
    const seg = schdata.WireSegment.new([start, schdata.Point.clone(start)]);

    const me = new DrawWire(schematic);
    const drawnSegment = schematic.wires.addProvisional(seg);
    me.segments.push(drawnSegment);
    schematic.select(drawnSegment);

    return me;
  }

  // Update the rendering of an in-progress wire.
  updateDrawWire = () => {
    // Get the active Wire, its points, and the second-to-last one for relative calculations.
    const { editor } = this;
    const { lastSeg } = this;

    // Sort out the closest Manhattan-separated point on the grid.
    const prev = lastSeg.start();
    const gridPoint = editor.grid.nearestManhattan(
      editor.uiState.mousePos.sch,
      prev
    );
    const landing = this.applyPinSnap(gridPoint);

    lastSeg.update({ endpoints: [prev, landing] });
  };

  // Add a new wire vertex to the currently-drawn wire.
  addWireVertex = () => {
    // Get the active Wire, its points, and the second-to-last one for relative calculations.
    const { editor, schematic } = this;
    const { lastSeg } = this;

    // Sort out the closest Manhattan-separated point on the grid.
    const prev = lastSeg.start();
    const gridPoint = editor.grid.nearestManhattan(
      editor.uiState.mousePos.sch,
      prev
    );
    const landing = this.applyPinSnap(gridPoint);

    if (Point.eq(landing, prev)) {
      return; // If landing is the same point, no need to make updates, we're done.
    }
    lastSeg.update({ endpoints: [prev, landing] });

    // Start a new segment
    const newSegData = schdata.WireSegment.new([
      landing,
      schdata.Point.clone(landing),
    ]);
    const newSeg = schematic.wires.addProvisional(newSegData);
    this.segments.push(newSeg);

    // Highlight the latest segment being drawn
    schematic.select(newSeg);

    // And draw it to the current cursor position.
    this.updateDrawWire();
  };

  // Commit the currently-drawn wire to the schematic.
  commitWire = () => {
    const { editor, schematic } = this;

    // Filter out any zero-length segments. Usually it's the last one, but check them all.
    const nonZeroLen: Array<WireSegment> = [];
    for (const seg of this.segments) {
      if (Point.eq(seg.start(), seg.end())) {
        seg.abort();
      } else {
        nonZeroLen.push(seg);
      }
    }

    // Pre-merge collinear overlapping segments before committing.
    // This prevents self-merges inside wires.add() which create contradictory
    // Add/Remove pairs in the change log, breaking undo/redo. Pin locations are
    // passed as protected points so a vertex anchored on a pin is not dissolved.
    const merged = Wires.mergeCollinear(
      nonZeroLen,
      schematic.wires.pinPointKeys(),
      // A merged run split at a pin can need one more segment than it had; these
      // are still provisional here, so a fresh provisional is the extra piece.
      (template, endpoints) =>
        schematic.wires.addProvisional({
          ...template.data,
          id: undefined,
          endpoints,
          labels: [],
        })
    );

    // Clear provisionals before committing — add() will place them in horiz/vert/nonManhattan.
    schematic.wires.clearProvisionals();

    // Add the merged segments to the schematic.
    schematic.wires.add(merged, true);

    // FIXME: update dots incrementally, instead of re-inferring them all!
    schematic.updateDots();

    // And go back to the UI Idle, nothing selected state.
    editor.deselect();
    editor.goUiIdle();
  };

  // Update the rendering of the wire on mouse-move.
  override handleMouseMove = () => this.updateDrawWire();
  // Add a vertex on mouse-up, i.e. the end of a click
  override handleMouseUp = () => this.addWireVertex();
  // Commit on double-clicks
  override handleDoubleClick = () => this.commitWire();
  // Handle keyboard input
  override handleKey = (e: KeyboardEvent) => {
    switch (e.key) {
      case Keys.Enter:
        // Commit the wire on Enter
        return this.commitWire();
      default:
        break;
    }
  };

  override abort = () => {
    const { editor, schematic } = this;
    schematic.wires.abortProvisionals();
    editor.deselect();
    editor.goUiIdle();
  };
}
